import { useEffect, useRef, useState } from "react";
import { api, apiUpload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, Edit3, Save, X, Upload, FileText, Trash2 } from "lucide-react";

// Very small markdown-ish renderer — headings, lists, bold. Keeps things simple.
function renderMarkdown(md) {
  const lines = (md || "").split("\n");
  const nodes = [];
  let listBuffer = null;
  const flushList = (i) => {
    if (listBuffer) {
      nodes.push(
        <ul key={`ul-${i}`} className="list-disc pl-6 space-y-1 my-3 text-gray-300">
          {listBuffer.map((li, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inline(li) }} />)}
        </ul>
      );
      listBuffer = null;
    }
  };
  const inline = (s) => s
    .replace(/\*\*([^*]+)\*\*/g, "<strong class='text-white'>$1</strong>")
    .replace(/`([^`]+)`/g, "<code class='bg-[#101015] px-1.5 py-0.5 text-[11px] font-mono border border-[#232330]'>$1</code>");
  lines.forEach((line, i) => {
    if (/^#\s+/.test(line)) {
      flushList(i);
      nodes.push(<h1 key={i} className="font-heading text-3xl font-black uppercase tracking-tighter mt-8 mb-4 text-cyan-400">{line.replace(/^#\s+/, "")}</h1>);
    } else if (/^##\s+/.test(line)) {
      flushList(i);
      nodes.push(<h2 key={i} className="font-heading text-xl font-black uppercase tracking-tight mt-6 mb-3 text-[#F4D03F] border-l-2 border-[#F4D03F] pl-3">{line.replace(/^##\s+/, "")}</h2>);
    } else if (/^-\s+/.test(line)) {
      if (!listBuffer) listBuffer = [];
      listBuffer.push(line.replace(/^-\s+/, ""));
    } else if (line.trim() === "") {
      flushList(i);
    } else {
      flushList(i);
      nodes.push(<p key={i} className="text-gray-300 my-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(line) }} />);
    }
  });
  flushList("end");
  return nodes;
}

export default function Rulebook() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [md, setMd] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfName, setPdfName] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = () => api.get("/rulebook").then((r) => {
    setMd(r.data.markdown || "");
    setUpdatedAt(r.data.updated_at);
    setPdfUrl(r.data.pdf_url || null);
    setPdfName(r.data.pdf_name || null);
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/admin/rulebook", { markdown: draft });
      toast.success("Rulebook updated.");
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const pickPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
      toast.error("Please select a PDF."); return;
    }
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF too large (max 15MB)."); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await apiUpload("/admin/rulebook/pdf", fd);
      toast.success("Rulebook PDF uploaded.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePdf = async () => {
    try {
      await api.delete("/admin/rulebook/pdf");
      toast.success("PDF removed.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="rulebook-root">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[#F4D03F] font-mono tracking-[0.3em] text-xs mb-2 flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> / OFFICIAL RULEBOOK
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Rulebook</h1>
          {updatedAt && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-2">
              Updated {new Date(updatedAt).toLocaleDateString()}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && !editing && (
            <Button onClick={() => { setDraft(md); setEditing(true); }}
                    data-testid="rulebook-edit"
                    className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold">
              <Edit3 className="w-3.5 h-3.5 mr-2" /> Edit rulebook
            </Button>
          )}
          {isAdmin && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={pickPdf}
                className="hidden"
                data-testid="rulebook-pdf-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="rulebook-pdf-upload"
                className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5 mr-2" /> {uploading ? "Uploading…" : pdfUrl ? "Replace PDF" : "Upload PDF"}
              </Button>
            </>
          )}
        </div>
      </div>

      {pdfUrl && (
        <div
          data-testid="rulebook-pdf-attached"
          className="border border-cyan-400/40 bg-cyan-400/5 p-4 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-6 h-6 text-cyan-400 shrink-0" />
            <div className="min-w-0">
              <div className="font-heading font-black uppercase tracking-tight truncate">
                {pdfName || "Official Rulebook.pdf"}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                Attached PDF · click to open
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <a
              href={`${backend}${pdfUrl}`}
              target="_blank"
              rel="noreferrer"
              data-testid="rulebook-pdf-download"
              className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black px-4 py-2 uppercase tracking-widest text-xs font-bold"
            >
              Open PDF
            </a>
            {isAdmin && (
              <button
                onClick={removePdf}
                data-testid="rulebook-pdf-remove"
                className="rounded-none border border-[#232330] hover:border-red-500 hover:text-red-500 text-gray-500 px-3 py-2 uppercase tracking-widest text-xs font-bold flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {editing ? (
        <div className="space-y-3">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={22}
            data-testid="rulebook-textarea"
            className="rounded-none bg-[#05050A] border-[#232330] font-mono text-sm leading-relaxed"
          />
          <div className="flex gap-2">
            <Button onClick={save} data-testid="rulebook-save"
                    className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold">
              <Save className="w-3.5 h-3.5 mr-2" /> Save
            </Button>
            <Button onClick={() => setEditing(false)} variant="outline"
                    className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest">
              <X className="w-3.5 h-3.5 mr-2" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <article className="border border-[#232330] bg-[#101015] p-8 lg:p-10 prose prose-invert max-w-none" data-testid="rulebook-body">
          {renderMarkdown(md)}
        </article>
      )}
    </div>
  );
}
